import { ApiResult } from "common/models";
import { EngHttpClient } from "common/services/eng-http-client.service";

export interface ClassificationDocument {
  guidCode: string;
  title: string;
  fileName: string;
  predictedCategory: string;
  confidence: number;
  reason: string;
  summary: string;
}

export interface ClassificationUploadRequest {
  files: File[];
  title: string;
  classificationName: string;
  metadata?: {
    company?: string;
    department?: string;
    createdBy?: string;
    batchNumber?: number;
    totalBatches?: number;
    parentBatchGuid?: string | null;
  };
}

export interface ClassificationUploadResponse {
  isUploaded: boolean;
  status: string;
  documentGuid: string;
  batchGuid: string;
}

export interface ClassificationListItem {
  batchGuid: string;
  classificationName: string;
  isProceeded: boolean;
  documentCount: number;
  createdBy: string;
  createdDate: string;
}

export interface ClassificationResult {
  id: string;
  fileName: string;
  category: string;
  confidence: number;
  reason: string;
  metadata?: {
    fileSize: number;
    fileType: string;
    extractedText?: string;
    tags?: string[];
  };
}

export interface ClassificationSummaryResponse {
  documentSummary: {
    totals: {
      completed: number;
      inProgress: number;
      error: number;
      queued: number;
    };
    categories: Array<{
      name: string;
      totalDocuments: number;
      statusCounts: {
        completed: number;
        inProgress: number;
        error: number;
        queued: number;
      };
    }>;
  };
}

export class ClassificationService extends EngHttpClient {
  /**
   * Upload documents for classification
   */
  public static uploadForClassification(
    request: ClassificationUploadRequest
  ): Promise<ApiResult<ClassificationUploadResponse[]>> {
    const formData = new FormData();

    request.files.forEach((file, index) => {
      formData.append(`Files`, file);
    });

    formData.append("title", request.title);
    formData.append("ClassificationName", request.classificationName);
    if (request.metadata?.company) {
      formData.append("company", request.metadata.company);
    }
    if (request.metadata?.department) {
      formData.append("department", request.metadata.department);
    }
    if (request.metadata?.createdBy) {
      formData.append("createdBy", request.metadata.createdBy);
    }
    if (request.metadata?.batchNumber) {
      formData.append("batchNumber", request.metadata.batchNumber.toString());
    }
    if (request.metadata?.totalBatches) {
      formData.append("totalBatches", request.metadata.totalBatches.toString());
    }
    if (request.metadata?.parentBatchGuid) {
      formData.append("parentBatchGuid", request.metadata.parentBatchGuid);
    }

    return this.invokeApi<ClassificationUploadResponse[]>(
      {
        method: "POST",
        url: "/api/Documents/UploadClassification",
        data: formData
      },
      {
        "Content-Type": "multipart/form-data"
      }
    );
  }

  /**
   * Upload documents for classification in batches with extended timeout
   */
  public static uploadForClassificationBatch(
    request: ClassificationUploadRequest
  ): Promise<ApiResult<ClassificationUploadResponse[]>> {
    const formData = new FormData();

    request.files.forEach((file, index) => {
      formData.append(`Files`, file);
    });

    formData.append("title", request.title);
    formData.append("ClassificationName", request.classificationName);
    if (request.metadata?.company) {
      formData.append("company", request.metadata.company);
    }
    if (request.metadata?.department) {
      formData.append("department", request.metadata.department);
    }
    if (request.metadata?.createdBy) {
      formData.append("createdBy", request.metadata.createdBy);
    }
    if (request.metadata?.batchNumber) {
      formData.append("batchNumber", request.metadata.batchNumber.toString());
    }
    if (request.metadata?.totalBatches) {
      formData.append("totalBatches", request.metadata.totalBatches.toString());
    }
    if (request.metadata?.parentBatchGuid) {
      formData.append("parentBatchGuid", request.metadata.parentBatchGuid);
    }

    return this.invokeApiWithTimeout<ClassificationUploadResponse[]>(
      {
        method: "POST",
        url: "/api/Documents/UploadClassification",
        data: formData
      },
      {
        "Content-Type": "multipart/form-data"
      },
      900000 // 15 minutes timeout for batch uploads
    );
  }

  /**
   * Get classification results for a document batch
   */
  public static getClassificationResults(
    documentId: string
  ): Promise<ApiResult<ClassificationResult[]>> {
    return this.invokeApi<ClassificationResult[]>({
      method: "GET",
      url: `api/Documents/ClassificationResults/${documentId}`
    });
  }

  /**
   * Get classification results by batch GUID
   */
  public static getClassificationResultsByBatch(
    batchGuid: string
  ): Promise<ApiResult<ClassificationResult[]>> {
    return this.invokeApi<ClassificationResult[]>({
      method: "GET",
      url: `api/Documents/ClassificationResults/batch/${batchGuid}`
    });
  }

  /**
   * Get classification status
   */
  public static getClassificationStatus(
    documentId: string
  ): Promise<ApiResult<{ status: string; progress?: number }>> {
    return this.invokeApi<{ status: string; progress?: number }>({
      method: "GET",
      url: `api/Documents/ClassificationStatus/${documentId}`
    });
  }

  /**
   * Reclassify a document
   */
  public static reclassifyDocument(
    documentId: string,
    newCategory?: string
  ): Promise<ApiResult<ClassificationResult>> {
    return this.invokeApi<ClassificationResult>({
      method: "PUT",
      url: `api/Documents/Reclassify/${documentId}`,
      data: { newCategory }
    });
  }

  /**
   * Get classification summary by batch GUID
   */
  public static getClassificationSummaryByBatch(
    batchGuid: string
  ): Promise<ApiResult<ClassificationSummaryResponse>> {
    return this.invokeApi<ClassificationSummaryResponse>({
      method: "GET",
      url: `api/Documents/ClassificationSummary?batchGuid=${batchGuid}`
    });
  }

  /**
   * Get classification documents using category and batch GUID as query parameters
   */
  public static getClassificationDocumentsByBatch(
    batchGuid: string,
    params?: {
      page?: number;
      pageSize?: number;
      category?: string;
    }
  ): Promise<ApiResult<ClassificationDocument[]>> {
    let url = `/api/Documents/classification-documents`;

    // Add query parameters - batchGuid and category are required
    const queryParams = new URLSearchParams();
    queryParams.append("batchGuid", batchGuid);

    if (params) {
      if (params.page !== undefined)
        queryParams.append("page", params.page.toString());
      if (params.pageSize !== undefined)
        queryParams.append("pageSize", params.pageSize.toString());
      if (params.category) queryParams.append("category", params.category);
    }

    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return this.invokeApi<ClassificationDocument[]>({
      method: "GET",
      url: url
    });
  }

  /**
   * Get classification list
   */
  public static getClassificationList(): Promise<
    ApiResult<ClassificationListItem[]>
  > {
    return this.invokeApi<ClassificationListItem[]>({
      method: "GET",
      url: "api/Documents/classification-list"
    });
  }

  /**
   * Batch extract all documents
   */
  public static batchExtractDocuments(
    batchGuids: string[]
  ): Promise<ApiResult<any>> {
    return this.invokeApi<any>({
      method: "POST",
      url: "api/Documents/ClassificationDocuments/Extract",
      data: { batchGuids }
    });
  }

  /**
   * Validate and get tool information by toolId/batchGUID
   */
  public static validateTool(toolId: string): Promise<ApiResult<any>> {
    return this.invokeApi<any>({
      method: "POST",
      url: `api/Documents/validate/tool/${toolId}`
    });
  }

  /**
   * Delete classification batch
   */
  public static deleteClassificationBatch(
    batchGuid: string
  ): Promise<ApiResult<void>> {
    return this.invokeApi<void>({
      method: "DELETE",
      url: `api/Documents/ClassificationBatch/${batchGuid}`
    });
  }
}
